import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { adminGet, adminPost, adminPut, adminDelete, adminPatch } from "../../api/adminClient";
import Icon from "../../components/AppIcon";
import { useToast } from "../../hooks/use-toast";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import { InlineLoader } from "../../components/ui/Loader";

const Settings = () => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState(null);
  const [searchAdmin, setSearchAdmin] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", role_id: "", is_active: true });
  const [formErrors, setFormErrors] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [toggleModalOpen, setToggleModalOpen] = useState(false);
  const [userToToggle, setUserToToggle] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, rolesData] = await Promise.all([
        // Always include inactive from backend; filter by status on frontend
        adminGet("/admin/settings/users?include_inactive=true"),
        adminGet("/admin/settings/roles"),
      ]);
      setUsers(Array.isArray(usersData) ? usersData : []);
      setRoles(Array.isArray(rolesData) ? rolesData : []);
    } catch (e) {
      if (e?.status !== 401 && !e?.isSessionExpired) setError(e?.message || "Failed to load users");
      if (e?.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      (u.email || "").toLowerCase().includes(searchAdmin.toLowerCase()) ||
      (u.full_name || "").toLowerCase().includes(searchAdmin.toLowerCase());
    const matchesRole = !filterRole || String(u.role_id) === filterRole;
    const matchesStatus =
      statusFilter === "all"
        ? true
        : statusFilter === "active"
        ? u.is_active
        : !u.is_active;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const openAddModal = () => {
    setEditingUser(null);
    setForm({ email: "", password: "", full_name: "", role_id: roles[0]?.role_id ?? "", is_active: true });
    setFormErrors({});
    setShowModal(true);
  };

  const openEditModal = (u) => {
    setEditingUser(u);
    setForm({
      email: u.email,
      password: "",
      full_name: u.full_name ?? "",
      role_id: u.role_id ?? "",
      is_active: u.is_active ?? true,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setForm({ email: "", password: "", full_name: "", role_id: "", is_active: true });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.email?.trim()) err.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) err.email = "Invalid email";
    if (!editingUser && !form.password) err.password = "Password is required";
    if (editingUser && form.password && form.password.length < 8) err.password = "Password must be at least 8 characters";
    if (!editingUser && form.password && form.password.length < 8) err.password = "Password must be at least 8 characters";
    setFormErrors(err);
    if (Object.keys(err).length) return;
    setShowModal(false);
    setLoading(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name?.trim() || null,
        role_id: form.role_id ? Number(form.role_id) : null,
        is_active: Boolean(form.is_active),
      };
      if (editingUser) {
        if (form.password) payload.password = form.password;
        await adminPut(`/admin/settings/users/${editingUser.user_id}`, payload);
      } else {
        payload.password = form.password;
        await adminPost("/admin/settings/users", payload);
      }
      closeModal();
      success(editingUser ? "Admin user updated successfully" : "Admin user created successfully");
      await load();
    } catch (err) {
      setFormErrors((prev) => ({ ...prev, _submit: err?.message || "Save failed" }));
      setShowModal(true);
      showError(err?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (u) => {
    setUserToDelete(u);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    setDeleteModalOpen(false);
    setLoading(true);
    try {
      await adminDelete(`/admin/settings/users/${userToDelete.user_id}`);
      setUserToDelete(null);
      success("Admin user deleted successfully");
      await load();
    } catch (e) {
      showError(e?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleClick = (u) => {
    setUserToToggle(u);
    setToggleModalOpen(true);
  };

  const handleConfirmToggle = async () => {
    if (!userToToggle) return;
    setToggleModalOpen(false);
    setLoading(true);
    try {
      await adminPatch(`/admin/settings/users/${userToToggle.user_id}/toggle-active`, {
        is_active: !userToToggle.is_active,
      });
      setToggleModalOpen(false);
      setUserToToggle(null);
      success(`User ${userToToggle.is_active ? "deactivated" : "activated"} successfully`);
      await load();
    } catch (e) {
      showError(e?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Settings</h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">Manage system configuration and admin users</p>
        </div>
        <div className="bg-card rounded-xl shadow-lg border border-border overflow-hidden">
          <div className="flex flex-col items-center justify-center py-12">
            <InlineLoader text="Loading admin users…" size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Settings</h1>
          <p className="text-text-secondary mt-1 text-sm sm:text-base">Manage system configuration and admin users</p>
        </div>
        <button
          type="button"
          onClick={openAddModal}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 shadow-md hover:shadow-lg transition-shadow shrink-0"
        >
          <Icon name="UserPlus" size={20} />
          Add Admin
        </button>
      </motion.div>

      {error && (
        <div className="p-3 rounded-xl bg-error/10 border border-error/20 text-error text-sm">{error}</div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="bg-card rounded-xl shadow-lg border border-border p-4 sm:p-6 space-y-4"
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row">
            <div className="relative w-full sm:max-w-xl">
              <Icon
                name="Search"
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
              />
              <input
                type="text"
                value={searchAdmin}
                onChange={(e) => setSearchAdmin(e.target.value)}
                placeholder="Search admins by name or email…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <div className="flex flex-1 gap-3">
              <div className="relative w-full max-w-[220px]">
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="w-full appearance-none rounded-lg border border-border bg-background px-3 py-2.5 pr-9 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">All roles</option>
                  {roles.map((r) => (
                    <option key={r.role_id} value={r.role_id}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <Icon
                  name="ChevronDown"
                  size={16}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary"
                />
              </div>
              <div className="relative w-full max-w-[180px]">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value || "all")}
                  className="w-full appearance-none rounded-lg border border-border bg-background px-3 py-2.5 pr-9 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="all">All statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <Icon
                  name="ChevronDown"
                  size={16}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <Icon name="Users" size={16} />
            <span>
              {filteredUsers.length} admin{filteredUsers.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="bg-card rounded-xl shadow-lg border border-border overflow-hidden"
      >
        <div className="px-4 sm:px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">Admin Users</h2>
          <p className="text-sm text-text-secondary mt-0.5">Manage admin user accounts and roles</p>
        </div>
        <div className="p-4 sm:p-6">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={28} className="text-primary" />
              </div>
              <p className="text-text-secondary mb-4">
                {searchAdmin || filterRole ? "No admins found matching your filters." : "No admin users found."}
              </p>
              {!searchAdmin && !filterRole && (
                <button
                  type="button"
                  onClick={openAddModal}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium shadow-md hover:shadow-lg transition-shadow"
                >
                  <Icon name="UserPlus" size={20} />
                  Create First Admin
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-sm font-medium text-text-primary">User</th>
                    <th className="px-4 py-3 text-sm font-medium text-text-primary">Email</th>
                    <th className="px-4 py-3 text-sm font-medium text-text-primary">Role</th>
                    <th className="px-4 py-3 text-sm font-medium text-text-primary">Status</th>
                    <th className="px-4 py-3 text-sm font-medium text-text-primary text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.user_id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Icon name="User" size={20} className="text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-text-primary">{u.full_name || "—"}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-text-primary">{u.email}</td>
                      <td className="px-4 py-3 text-text-secondary">{u.role_name || "—"}</td>
                      <td className="px-4 py-3 text-text-secondary">
                        {u.is_active ? <span className="text-success">Active</span> : <span className="text-text-secondary">Inactive</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleToggleClick(u)}
                            title={u.is_active ? "Deactivate" : "Activate"}
                            className="p-2 rounded-lg hover:bg-muted text-primary transition-colors"
                          >
                            <Icon name={u.is_active ? "UserMinus" : "UserPlus"} size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditModal(u)}
                            title="Edit"
                            className="p-2 rounded-lg hover:bg-muted text-primary transition-colors"
                          >
                            <Icon name="Edit" size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(u)}
                            title="Delete"
                            className="p-2 rounded-lg hover:bg-muted text-error transition-colors"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[999]">
          <div className="bg-card rounded-xl shadow-lg border border-border p-6 w-full max-w-lg admin-layout text-text-primary" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-text-primary">{editingUser ? "Edit Admin User" : "Add Admin User"}</h3>
              <button type="button" onClick={closeModal} className="p-2 rounded-lg hover:bg-muted">
                <Icon name="X" size={20} />
              </button>
            </div>
            {formErrors._submit && (
              <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm">{formErrors._submit}</div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  disabled={!!editingUser}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary disabled:opacity-60"
                />
                {formErrors.email && <p className="text-xs text-error mt-1">{formErrors.email}</p>}
                {editingUser && <p className="text-xs text-text-secondary mt-1">Email cannot be changed</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Full Name</label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Role</label>
                <select
                  value={form.role_id}
                  onChange={(e) => setForm({ ...form, role_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                >
                  <option value="">— No role —</option>
                  {roles.map((r) => (
                    <option key={r.role_id} value={r.role_id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Password *</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                  />
                  {formErrors.password && <p className="text-xs text-error mt-1">{formErrors.password}</p>}
                </div>
              )}
              {editingUser && (
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">New Password (leave blank to keep)</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text-primary"
                  />
                  {formErrors.password && <p className="text-xs text-error mt-1">{formErrors.password}</p>}
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="rounded border-border"
                />
                <span className="text-sm text-text-primary">Active</span>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={closeModal} className="px-4 py-2 rounded-lg border border-border hover:bg-muted">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!form.email?.trim() || (!editingUser && !form.password)}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  {editingUser ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModalOpen && !!userToDelete}
        title="Delete Admin User"
        message={`Are you sure you want to delete "${userToDelete?.email}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteModalOpen(false); setUserToDelete(null); }}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <ConfirmationModal
        isOpen={toggleModalOpen && !!userToToggle}
        title={userToToggle?.is_active ? "Deactivate User" : "Activate User"}
        message={`Are you sure you want to ${userToToggle?.is_active ? "deactivate" : "activate"} "${userToToggle?.email}"?`}
        onConfirm={handleConfirmToggle}
        onCancel={() => { setToggleModalOpen(false); setUserToToggle(null); }}
        confirmText={userToToggle?.is_active ? "Deactivate" : "Activate"}
        cancelText="Cancel"
        confirmVariant={userToToggle?.is_active ? "danger" : "success"}
      />
    </div>
  );
};

export default Settings;
